/**
 * Division Switcher — sits in the app header, next to the tenant switcher.
 * Persists selection in localStorage as `altara.active_entity_id`.
 * Downstream screens can read the value via `getActiveEntityId()`.
 */
import { useEffect, useState } from "react";
import { Building2, ChevronDown, Check } from "lucide-react";
import api from "@/lib/api";

const KEY = "altara.active_entity_id";

export function getActiveEntityId() {
  try { return localStorage.getItem(KEY) || null; } catch { return null; }
}
export function setActiveEntityId(id) {
  try { if (id) localStorage.setItem(KEY, id); else localStorage.removeItem(KEY); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent("altara:entity-change", { detail: { entity_id: id } }));
}

export default function DivisionSwitcher() {
  const [entities, setEntities] = useState([]);
  const [active, setActive] = useState(getActiveEntityId());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/regulatory/entities")
      .then((r) => setEntities(r.data.entities || []))
      .catch(() => {});
  }, []);

  const activeEntity = entities.find((e) => e.entity_id === active);
  const label = activeEntity ? activeEntity.legal_name : "All entities";

  const pick = (id) => {
    setActive(id);
    setActiveEntityId(id);
    setOpen(false);
  };

  if (entities.length === 0) return null;   // hide until entities exist

  return (
    <div className="relative" data-testid="division-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] hover:border-slate-400 transition"
        data-testid="division-switcher-toggle"
      >
        <Building2 className="w-3.5 h-3.5 text-slate-500" />
        <span className="font-semibold text-slate-800 truncate max-w-[180px]">{label}</span>
        <ChevronDown className="w-3 h-3 text-slate-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 right-0 z-50 w-64 rounded-lg border border-slate-200 bg-white shadow-lg py-1"
               data-testid="division-switcher-menu">
            <MenuItem active={!active} onClick={() => pick(null)} label="All entities" caption="Consolidated view" />
            <div className="my-1 border-t border-slate-100" />
            {entities.map((e) => (
              <MenuItem
                key={e.entity_id}
                active={e.entity_id === active}
                onClick={() => pick(e.entity_id)}
                label={e.legal_name}
                caption={[e.entity_type, e.fsp_number && `FSP ${e.fsp_number}`].filter(Boolean).join(" · ")}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MenuItem({ label, caption, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-slate-50"
    >
      <div className={`w-4 h-4 mt-0.5 flex items-center justify-center shrink-0 ${active ? "text-emerald-600" : "text-transparent"}`}>
        <Check className="w-3.5 h-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold text-slate-800 truncate">{label}</div>
        {caption && <div className="text-[10.5px] text-slate-500 truncate">{caption}</div>}
      </div>
    </button>
  );
}
